import React from 'react'
import clsx from 'clsx'
import { Chip } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { AdvancedValue } from '@SonarCX/pages/Customers/FieldFilters/FieldPills'

import { dissectFilter } from '../../helpers/unparse'
import { selectViews, fetchViews, deleteView } from '@SonarCX/slices/views'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useTaskFilters } from '@SonarCX/providers/TaskFiltersProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useStyles from './styles'

type ViewsProps = {
  fieldValues: AdvancedValue[]
  setFieldValues: React.Dispatch<React.SetStateAction<AdvancedValue[]>>
  setTempSearch: React.Dispatch<React.SetStateAction<string>>
  onLoad?: () => void
  hideTitle?: boolean
}

const Views: React.FC<ViewsProps> = ({
  setFieldValues,
  setTempSearch,
  onLoad,
  hideTitle,
  fieldValues = []
}) => {
  const dispatch = useAppDispatch()

  const {
    views,
    loaded: viewsLoaded,
    loading: viewsLoading
  } = useAppSelector(selectViews)

  const { user } = useEnvironment()
  const {
    state: filterState,
    includeSubtasks,
    setState,
    setIncludeSubtasks
  } = useTaskFilters()
  const { setTitle, setReason, setSubtext, setV2, setAction } = useAreYouSure()
  const [selectedView, setSelectedView] = React.useState<string>(null)
  const taskViews = React.useMemo(
    () =>
      views.filter(
        view => view.type === 'task'
      ) as APIView<APITaskViewFilter>[],
    [views]
  )

  const classes = useStyles()

  // Load all views if needed
  React.useEffect(() => {
    if (!viewsLoaded && !viewsLoading) {
      dispatch(fetchViews())
    }
  }, [viewsLoaded, viewsLoading, dispatch])

  const loadView = React.useCallback(
    (view: APIView<APITaskViewFilter>) => {
      const { state, includeSubtasks, fieldValues } = dissectFilter(
        view.filter,
        user.id
      )

      setSelectedView(view.id)
      setState(state)
      setIncludeSubtasks(includeSubtasks)
      setFieldValues(fieldValues)
      setTempSearch(state.searchFilter)

      onLoad && onLoad()
    },
    [user, setState, setIncludeSubtasks, setFieldValues, setTempSearch, onLoad]
  )

  const handleDeleteView = React.useCallback(
    (view: APIView<APITaskViewFilter>) => {
      setTitle('DELETE VIEW?')
      setReason(`Delete ${view.name}`)
      setSubtext(`Are you sure you want to delete this view?`)
      setV2(true)
      setAction(() => {
        dispatch(deleteView(view.id))
      })
    },
    [setTitle, setReason, setSubtext, setV2, setAction, dispatch]
  )

  React.useEffect(() => {
    const taskViews = views.filter(x => x.type === 'task')
    if (taskViews.length > 0 && user) {
      const found = taskViews.find(view => {
        const {
          state,
          includeSubtasks: savedIncludeSubtasks,
          fieldValues: savedFieldValues
        } = dissectFilter(view.filter, user.id)
        if (
          JSON.stringify(state) === JSON.stringify(filterState) &&
          JSON.stringify(fieldValues) === JSON.stringify(savedFieldValues) &&
          includeSubtasks === savedIncludeSubtasks
        ) {
          setSelectedView(view.id)

          return true
        }
        return false
      })
      if (!found) {
        setSelectedView(null)
      }
    }
  }, [views, filterState, user, fieldValues, includeSubtasks])

  if (taskViews.length === 0) {
    return null
  }

  return (
    <div className={clsx(classes.root, !hideTitle && classes.rootWithTitle)}>
      {!hideTitle && <div className={classes.title}>VIEWS</div>}

      <div
        className={clsx(classes.views, !hideTitle && classes.viewsWithTitle)}
      >
        {taskViews.map(view => (
          <Chip
            key={view.id}
            color={selectedView === view.id ? 'primary' : 'secondary'}
            onClick={() => loadView(view)}
            onDelete={e => {
              e.stopPropagation()
              e.preventDefault()
              handleDeleteView(view)
            }}
            label={view.name}
          />
        ))}
      </div>
    </div>
  )
}

export default Views
